import React from 'react'
import { Box, Button, Card, CardContent, Chip, Grid, IconButton, InputAdornment, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CheckIcon from '@mui/icons-material/Check'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import { STATUS_STYLES, BUCKETS, BUCKET_LIST, ROUTES_FLOW } from '../constants'
import { NOTES } from '../apiMap'

/**
 * helperProps — spread onto a TextField/Select to render its helperText as a compact,
 * single-line label/hint (11px, muted, ellipsis-truncated) instead of MUI's default
 * form-helper styling, which reads at the same size/weight as real field content.
 * Full text is still available on hover via the native title tooltip.
 * When the sibling field is in an error state, MUI auto-applies .Mui-error to this
 * helper text — the override below lets an error message wrap in full instead of
 * being cut off, since an unreadable "Must add up …" defeats the point of the flag.
 */
export const helperSx = {
  fontSize: 11, lineHeight: 1.3, color: 'text.secondary', mt: '4px', display: 'block', maxWidth: '100%',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  '&.Mui-error': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', wordBreak: 'break-word', color: 'error.main' },
}
export const helperProps = (text) => ({ helperText: text, FormHelperTextProps: { sx: helperSx, title: text } })

// infoAdorn — spread into a TextField's InputProps to surface help behind an ⓘ tooltip at the
// field's right edge (optionally next to a unit like % / ₹), instead of an inline helper line.
// Pair it with dropping the help from helperProps (keep ONLY the validation error there), so a
// field reads as just "label + ⓘ" until it actually needs to flag something.
export const infoAdorn = (help, unit) => ({
  endAdornment: (
    <InputAdornment position="end" disableTypography sx={{ fontSize: 12, color: 'text.secondary' }}>
      {unit || null}
      {help ? <NoteInfoIcon body={help} /> : null}
    </InputAdornment>
  ),
})

// Compact fields. The floating field LABELS (and placeholders) default to ~16px+, which reads
// oversized next to this module's 11–12px helpers. Spread onto the shared field containers
// (SectionCard, ServiceBlock, dialogs) so every field's label + placeholder is toned down in
// one place — no per-TextField edits. Labels use fontSize (the shrink transform then scales
// the floating state down further).
export const compactPlaceholderSx = {
  '& .MuiInputLabel-root': { fontSize: '12px' },
  '& ::placeholder': { fontSize: '12px' },
  '& .MuiInputBase-input': { fontSize: '12px' },
}

/**
 * Pill — a small status/bucket chip with the module's colour language.
 * Pass either `styleKey` (a STATUS_STYLES key) or `bucketKey` (a BUCKETS key).
 */
export function Pill({ label, styleKey, bucketKey, size = 'small', sx }) {
  let s = STATUS_STYLES.neutral
  if (bucketKey && BUCKETS[bucketKey]) s = { color: BUCKETS[bucketKey].color, bg: BUCKETS[bucketKey].bg }
  else if (styleKey && STATUS_STYLES[styleKey]) s = STATUS_STYLES[styleKey]
  return (
    <Chip
      label={label}
      size={size}
      sx={{
        bgcolor: s.bg,
        color: s.color,
        fontWeight: 600,
        fontSize: 12,
        height: 22,
        borderRadius: '11px',
        '& .MuiChip-label': { px: 1.1 },
        ...sx,
      }}
    />
  )
}

/** SectionCard — a titled MUI card matching the admin panel's card style. */
export function SectionCard({ title, subtitle, action, children, sx, accent = 'primary.main' }) {
  return (
    <Card sx={{
      mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 6px 18px rgba(16,24,40,0.05)',
      transition: 'box-shadow .18s ease, transform .18s ease',
      '&:hover': { boxShadow: '0 2px 4px rgba(16,24,40,0.05), 0 12px 28px rgba(16,24,40,0.09)' },
      ...compactPlaceholderSx, ...sx,
    }}>
      <CardContent sx={{ p: 2.25 }}>
        {(title || action) && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: subtitle ? 0.5 : 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, flex: 1, minWidth: 0 }}>
              {/* Accent marker — a spot of brand colour so each section reads as a real heading. */}
              {title && <Box sx={{ width: 4, borderRadius: 2, bgcolor: accent, flexShrink: 0 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {title && (
                  <Typography variant="h6" sx={{ fontSize: 15.5, fontWeight: 700, color: 'primary.dark' }}>
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>
        )}
        {children}
      </CardContent>
    </Card>
  )
}

/**
 * ExportBar — the ONE export control for a wizard step. Placed at the top-right of every
 * step so the "download this page's data" action always sits in the same spot, and given a
 * deliberately distinct look (pill shape + download icon + soft blue fill) so it never reads
 * as one of the square primary/secondary buttons. `label` lets a step name its own file.
 */
export function ExportBar({ onClick, label = 'Export this step (.xlsx)', disabled = false, sx }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.75, ...sx }}>
      <Button
        onClick={onClick}
        disabled={disabled}
        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: 'none', fontSize: 13, fontWeight: 700, borderRadius: '999px',
          px: 2, py: 0.65, color: '#0B5CD6', bgcolor: '#EEF4FE',
          border: '1.5px solid #B8D2F5', boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
          '&:hover': { bgcolor: '#E0ECFD', borderColor: '#0B5CD6' },
          '&.Mui-disabled': { color: '#9AA6B8', bgcolor: '#F3F5F9', borderColor: '#E3E8F0' },
        }}
      >
        {label}
      </Button>
    </Box>
  )
}

/** KpiRow — four KPI tiles like the mockup dashboards. */
// Accent palette cycled per tile (blue / green / purple / amber) so the row reads
// colourful and scannable; a tile may override with its own `color`.
const KPI_ACCENTS = ['#1565c0', '#16a34a', '#6d28d9', '#b45309']
export function KpiRow({ items = [] }) {
  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {items.map((k, i) => {
        const accent = k.color || KPI_ACCENTS[i % KPI_ACCENTS.length]
        return (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{
              position: 'relative', overflow: 'hidden', height: '100%',
              borderRadius: '12px', border: '1px solid #E7EAF0', borderLeft: `4px solid ${accent}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'box-shadow .18s ease, transform .18s ease',
              '&:hover': { boxShadow: '0 10px 26px rgba(16,24,40,0.10)', transform: 'translateY(-2px)' },
            }}>
              {/* Soft decorative accent glow in the corner. */}
              <Box sx={{ position: 'absolute', top: -24, right: -24, width: 78, height: 78, borderRadius: '50%', bgcolor: accent, opacity: 0.08, pointerEvents: 'none' }} />
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, position: 'relative' }}>
                <Typography sx={{ fontSize: 11, color: '#8A94A6', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                  {k.label}
                </Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: accent, mt: 0.4, lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                  {k.value}
                </Typography>
                {k.delta && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 0.4, color: k.dir === 'up' ? 'success.main' : k.dir === 'dn' ? 'error.main' : 'text.secondary' }}>
                    {k.delta}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )
      })}
    </Grid>
  )
}

/** MarginBuckets — the five colour-coded buckets the Sales Leader sees. */
export function MarginBuckets() {
  return (
    <Grid container spacing={1.25}>
      {BUCKET_LIST.map((b) => (
        <Grid item xs={6} md key={b.key}>
          <Box sx={{ borderRadius: '10px', p: 1.25, border: '1px solid', borderColor: 'divider', borderTop: `4px solid ${b.color}`, bgcolor: b.bg, height: '100%' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 12, color: b.color }}>{b.label}</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>{b.range}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

/** RouteBands — the three approval routes (reference legend).
 *  Pass activeRoute (1|2|3) to highlight this deal's route and dim the others, so the
 *  static ranges read clearly as a legend — never as the deal's own margin band. */
export function RouteBands({ activeRoute = null }) {
  const tone = { info: '#1D4ED8', warning: '#A16207', error: '#B91C1C' }
  const bg = { info: '#DBEAFE', warning: '#FEF9C3', error: '#FEE2E2' }
  return (
    <Grid container spacing={1.25}>
      {ROUTES_FLOW.map((r, i) => {
        const isActive = activeRoute != null && i + 1 === activeRoute
        const dim = activeRoute != null && !isActive
        return (
          <Grid item xs={12} md={4} key={r.key}>
            <Box sx={{ borderRadius: '10px', p: 1.5, border: isActive ? '2px solid' : '1px solid', borderColor: isActive ? tone[r.color] : 'divider', borderTop: `4px solid ${tone[r.color]}`, bgcolor: bg[r.color], height: '100%', opacity: dim ? 0.5 : 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 12, color: tone[r.color], flex: 1 }}>{r.label}</Typography>
                {isActive && <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#fff', bgcolor: tone[r.color], borderRadius: '6px', px: 0.6, py: 0.1 }}>THIS DEAL</Typography>}
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{r.range}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5, lineHeight: 1.45 }}>{r.desc}</Typography>
            </Box>
          </Grid>
        )
      })}
    </Grid>
  )
}

/** A thin, theme-consistent helper for the standard ghost/primary buttons row. */
export function ActionsRow({ children, justify = 'flex-end', sx }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: justify, flexWrap: 'wrap', mt: 1, ...sx }}>
      {children}
    </Box>
  )
}

/**
 * GapNote — a precise, in-UI note explaining where the UI offers something the
 * API does not (yet) accept. Pass `id` (a key in apiMap NOTES) or raw title/body.
 * Amber, info-style banner; never blocks — purely informational.
 */
export function GapNote({ id, title, body, sx }) {
  const note = id ? NOTES[id] : null
  const t = title || note?.title
  const b = body || note?.body
  if (!t && !b) return null
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px', px: 1.5, py: 1, my: 1, ...sx }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: '#C2410C', mt: '1px' }} />
      <Box>
        {t && <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#9A3412' }}>{t}</Typography>}
        {b && <Typography sx={{ fontSize: 12, color: '#9A3412', lineHeight: 1.5 }}>{b}</Typography>}
      </Box>
    </Box>
  )
}

/**
 * NoteInfoIcon — the same note text as GapNote, but tucked behind a hover/click info
 * icon so it isn't always on screen. Pass `id` (a key in apiMap NOTES) or raw title/body.
 */
export function NoteInfoIcon({ id, title, body, sx }) {
  const note = id ? NOTES[id] : null
  const t = title || note?.title
  const b = body || note?.body
  if (!t && !b) return null
  return (
    <Tooltip
      arrow
      title={<Box sx={{ py: 0.25 }}>{t && <Typography sx={{ fontWeight: 700, fontSize: 12, mb: b ? 0.5 : 0 }}>{t}</Typography>}{b && <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>{b}</Typography>}</Box>}
    >
      <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle', ml: 0.5, ...sx }} />
    </Tooltip>
  )
}

/**
 * ChoiceCards — the module's ONE "make a selection" control. A labelled radio-group of cards,
 * each with a real radio dot + a strong selected state (2px accent ring + tint + accent title),
 * so major decisions (commercial type, pricing method, RVP structure, prospect size…) read
 * clearly as CHOICES and never blend into the quiet input fields around them.
 * options: [{ value, title, desc?, disabled? }]
 */
export function ChoiceCards({ label, help, value, onChange, options = [], minWidth = 210, sx }) {
  return (
    <Box sx={{ mb: 1.5, ...sx }}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.85 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary' }}>{label}</Typography>
          {help ? <NoteInfoIcon body={help} /> : null}
        </Box>
      )}
      <Box role="radiogroup" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'stretch' }}>
        {options.map((o) => {
          const on = value === o.value
          const Icon = o.icon
          return (
            <Box
              key={o.value}
              role="radio"
              aria-checked={on}
              aria-disabled={o.disabled || undefined}
              tabIndex={o.disabled ? -1 : 0}
              onClick={() => { if (!o.disabled) onChange(o.value) }}
              onKeyDown={(e) => { if (!o.disabled && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); onChange(o.value) } }}
              sx={{
                flex: `1 1 ${minWidth}px`, minWidth, cursor: o.disabled ? 'not-allowed' : 'pointer',
                display: 'flex', gap: 1, alignItems: 'flex-start', opacity: o.disabled ? 0.5 : 1,
                border: '1.5px solid', borderColor: on ? 'primary.main' : 'divider', borderRadius: '12px', p: 1.5,
                bgcolor: on ? (t) => alpha(t.palette.primary.main, 0.08) : 'background.paper',
                boxShadow: on ? (t) => `inset 0 0 0 1px ${t.palette.primary.main}, 0 8px 20px ${alpha(t.palette.primary.main, 0.16)}` : '0 1px 2px rgba(16,24,40,0.05)',
                transition: 'border-color .14s, box-shadow .14s, background-color .14s, transform .14s',
                '&:hover': o.disabled ? {} : { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: (t) => `0 8px 18px ${alpha(t.palette.primary.main, 0.14)}` },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
              }}
            >
              <Box sx={{ mt: '2px', width: 16, height: 16, flexShrink: 0, borderRadius: '50%', border: '1.5px solid', borderColor: on ? 'primary.main' : 'action.disabled', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {on && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {Icon && <Icon sx={{ fontSize: 18, color: on ? 'primary.main' : 'text.secondary' }} />}
                  <Typography sx={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.3, color: on ? 'primary.main' : 'text.primary' }}>{o.title}</Typography>
                </Box>
                {o.desc && <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>{o.desc}</Typography>}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

/**
 * SelectCards — elevated "pick one" cards: an icon chip + title + one-line hint, with a check
 * badge and a lift when selected. For a small set of prominent, described choices (e.g. the
 * Step-2 split-entry mode). options: [{ value, title, desc?, icon? }]
 */
export function SelectCards({ value, onChange, options = [], minWidth = 168, sx }) {
  return (
    <Box role="radiogroup" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, ...sx }}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            sx={{
              position: 'relative', flex: `1 1 ${minWidth}px`, minWidth, textAlign: 'left', font: 'inherit', cursor: 'pointer',
              display: 'flex', gap: 1.25, alignItems: 'flex-start', p: '14px', borderRadius: '14px',
              border: '1.5px solid', borderColor: on ? 'primary.main' : 'divider',
              bgcolor: on ? (t) => alpha(t.palette.primary.main, 0.06) : 'background.paper',
              boxShadow: on ? (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.16)}` : 'none',
              transition: 'border-color .14s, box-shadow .14s, transform .14s, background-color .14s',
              '&:hover': { borderColor: on ? 'primary.main' : 'primary.light', transform: 'translateY(-1px)' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
            }}
          >
            {o.icon && (
              <Box sx={{ width: 32, height: 32, flex: '0 0 auto', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: on ? 'primary.main' : 'action.hover', color: on ? '#fff' : 'text.secondary', '& svg': { fontSize: 18 } }}>
                {o.icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: on ? 'primary.main' : 'text.primary' }}>{o.title}</Typography>
              {o.desc && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25, lineHeight: 1.35 }}>{o.desc}</Typography>}
            </Box>
            {on && (
              <Box sx={{ position: 'absolute', top: 9, right: 9, width: 17, height: 17, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckIcon sx={{ fontSize: 12 }} />
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

/**
 * SegmentedControl — a compact connected "pick one" bar; the selected segment fills accent.
 * For short mutually-exclusive choices / mode switches where per-option descriptions aren't
 * needed. Pairs with ChoiceCards (used for bigger decisions that carry a description).
 * options: [{ value, label }]
 */
export function SegmentedControl({ value, onChange, options = [], sx }) {
  return (
    <Box role="radiogroup" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px', p: '4px', bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: '10px', ...sx }}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            sx={{
              font: 'inherit', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', border: 'none',
              px: 1.75, py: 0.85, borderRadius: '7px', transition: 'background-color .12s, color .12s',
              color: on ? '#fff' : 'text.secondary', bgcolor: on ? 'primary.main' : 'transparent',
              boxShadow: on ? '0 1px 3px rgba(32,101,209,0.35)' : 'none',
              '&:hover': { color: on ? '#fff' : 'text.primary' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
            }}
          >
            {o.label}
          </Box>
        )
      })}
    </Box>
  )
}

/**
 * ActionIconBtn — a tooltip'd icon button for table action columns, so rows stay neat
 * (no wrapping text links). Stops row-click propagation. Pass the MUI icon as children.
 */
export function ActionIconBtn({ title, onClick, disabled, color = 'primary', children }) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={title}
          onClick={(e) => { e.stopPropagation(); onClick && onClick(e) }}
          sx={{ color: disabled ? 'text.disabled' : `${color}.main` }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  )
}

/** PrimaryActionIcon — picks the icon for a proposal's dynamic primary action label. */
export function PrimaryActionIcon({ action }) {
  const a = String(action || '').toLowerCase()
  if (a.includes('resume')) return <EditOutlinedIcon fontSize="small" />
  if (a.includes('revise') || a.includes('resubmit')) return <ReplayOutlinedIcon fontSize="small" />
  return <VisibilityOutlinedIcon fontSize="small" /> // View / Open / default
}
